<script setup lang="ts">
import { ref, computed, toRef } from "vue";
import {
  useCreateSection,
  useUpdateSection,
  useDeleteSection,
} from "@/stores/queries/sections.query";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import DRDropdownItem from "@/components/UI/DRDropdownItem.vue";
import DREditTextDialog from "@/components/UI/feedbacks/DREditTextDialog.vue";
import ConfirmationModal from "@/components/generic/ConfirmationModal.vue";
import { useTranslation } from "i18next-vue";
import type { SectionItem } from "@/types/sections.types";
const { t } = useTranslation();

const props = defineProps<{
  section: SectionItem;
  reportId: number;
  size?: number;
  findingsActions?: boolean;
}>();

const emit = defineEmits<{
  (e: "create-finding"): void;
}>();

const sectionId = toRef(() => props.section.id);

const { mutateAsync: createSection } = useCreateSection();
const { mutateAsync: updateSection, isPending: isUpdating } =
  useUpdateSection(sectionId);
const { mutateAsync: deleteSection, isPending: isDeleting } =
  useDeleteSection(sectionId);

// Dialog state
const showAddDialog = ref(false);
const showRenameDialog = ref(false);
const showDeleteConfirm = ref(false);
const addingMode = ref<"child" | "above" | "below" | null>(null);

const dialogTitle = computed(() => {
  if (addingMode.value === "child") return t("report:photos.sections.addChild");
  if (addingMode.value === "above") return t("report:photos.sections.addAbove");
  if (addingMode.value === "below") return t("report:photos.sections.addBelow");
  return "";
});

const handleCreate = async (title: string) => {
  if (!title.trim()) return;

  let parentId: number | undefined = undefined;
  let order = 0;

  if (addingMode.value === "child") {
    parentId = props.section.id;
    order = 0;
  } else if (addingMode.value === "above") {
    parentId = props.section.parentId ?? undefined;
    order = props.section.order;
  } else if (addingMode.value === "below") {
    parentId = props.section.parentId ?? undefined;
    order = props.section.order + 1;
  }

  await createSection({
    reportId: props.reportId,
    title: title.trim(),
    order,
    parentId,
  });

  addingMode.value = null;
};

const startAddChild = () => {
  addingMode.value = "child";
  showAddDialog.value = true;
};

const startAddAbove = () => {
  addingMode.value = "above";
  showAddDialog.value = true;
};

const startAddBelow = () => {
  addingMode.value = "below";
  showAddDialog.value = true;
};

const handleRename = async (newTitle: string) => {
  if (!newTitle.trim()) return;
  await updateSection({ title: newTitle.trim() });
};

const handleDelete = async () => {
  await deleteSection();
  showDeleteConfirm.value = false;
};
</script>

<template>
  <el-dropdown trigger="click">
    <DRBtnIcon
      icon="bi-three-dots"
      variant="flat"
      :size="props.size ?? 24"
      class="action-btn"
      @click.stop
    />
    <template #dropdown>
      <el-dropdown-menu>
        <!-- Findings action (only if enabled) -->
        <template v-if="props.findingsActions">
          <DRDropdownItem
            icon="bi-plus-lg"
            :label="$t('report:findings.addFinding')"
            @select="emit('create-finding')"
          />
          <div class="dropdown-divider"></div>
        </template>

        <DRDropdownItem
          icon="bi-pencil"
          :label="$t('report:photos.sections.rename')"
          :loading="isUpdating"
          @select="showRenameDialog = true"
        />
        <div class="dropdown-divider"></div>

        <DRDropdownItem
          icon="bi-arrow-bar-right"
          :label="$t('report:photos.sections.addChild')"
          @select="startAddChild"
        />
        <DRDropdownItem
          icon="bi-arrow-up"
          :label="$t('report:photos.sections.addAbove')"
          @select="startAddAbove"
        />
        <DRDropdownItem
          icon="bi-arrow-down"
          :label="$t('report:photos.sections.addBelow')"
          @select="startAddBelow"
        />

        <div class="dropdown-divider"></div>

        <DRDropdownItem
          icon="bi-trash"
          :label="$t('report:photos.sections.delete')"
          type="danger"
          @select="showDeleteConfirm = true"
        />
      </el-dropdown-menu>
    </template>
  </el-dropdown>

  <!-- Add section dialog -->
  <DREditTextDialog
    v-model="showAddDialog"
    :title="dialogTitle"
    :placeholder="$t('report:photos.sections.newSectionName')"
    @confirm="handleCreate"
  />

  <!-- Rename section dialog -->
  <DREditTextDialog
    v-model="showRenameDialog"
    :title="$t('report:photos.sections.rename')"
    :placeholder="$t('report:photos.sections.newSectionName')"
    :value="section.title"
    @confirm="handleRename"
  />

  <!-- Delete confirmation modal -->
  <ConfirmationModal
    v-model="showDeleteConfirm"
    :title="$t('report:photos.sections.deleteConfirm.title')"
    :message="$t('report:photos.sections.deleteConfirm.message')"
    :confirm-label="$t('delete')"
    :loading="isDeleting"
    danger
    @confirm="handleDelete"
  />
</template>

<style lang="scss" scoped>
.dropdown-divider {
  height: 1px;
  margin: 4px 0;
  background-color: var(--el-border-color);
}
</style>
